import { makeAutoObservable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import type { RootStore } from "./root.store";
import type {
  TCurrentUserSessionPolicy,
  TWorkspaceSessionPolicy,
  TWorkspaceSessionPolicyUpdate,
} from "@/types/workspace";

export class WorkspaceSessionPolicyStore {
  policy: TWorkspaceSessionPolicy | null = null;
  // The slug `policy` actually belongs to — distinct from "loaded successfully
  // with an empty policy" (a real, valid response). The form gates rendering
  // and saving on this matching the page's own slug, so a workspace switch or
  // a failed load can never leave a foreign workspace's values on screen to
  // be edited and PATCHed into the wrong workspace.
  policySlug: string | null = null;
  // The slug a fetch has actually STARTED for, set synchronously the moment
  // one does — distinct from `policySlug`, which only advances on success.
  // Lets a caller tell "nothing has been tried for this slug yet" (about to
  // load) apart from "tried and failed" (genuinely broken): without it, a
  // component's very first render — before any effect has had a chance to
  // call fetchPolicy at all — reads identically to a real failure
  // (`policySlug` unset, `isLoading` false either way) and flashes an error
  // state that a real load was never given the chance to prevent.
  attemptedSlug: string | null = null;
  isLoading = false;
  isSaving = false;
  // The CALLER's own enforced thresholds (users/me/session-policy/, NG-1955) —
  // user-scoped, not per-workspace, so none of the slug bookkeeping above
  // applies. Feeds useIdleTimeout for every signed-in user; the per-slug
  // `policy` above stays the admin settings page's editable workspace config.
  effectivePolicy: TCurrentUserSessionPolicy | null = null;

  private rootStore: RootStore;
  // Orders every write to `policy`/`policySlug` (a fetch or an update, any
  // slug): only the call holding the CURRENT generation when it resolves may
  // commit, so an older response — a GET started before a later PATCH, or a
  // GET for a workspace since navigated away from — can never overwrite a
  // newer one.
  private generation = 0;
  // The workspace layout's clientLoader and the settings page's own loader
  // can both call fetchPolicy for the SAME slug concurrently. Two independent
  // GETs racing for the same data don't "supersede" each other the way a
  // workspace switch does — whichever happens to fail first would otherwise
  // still discard the other's success purely by generation order. Share one
  // in-flight request per slug instead (mirrors WorkspaceStore's
  // fetchWorkspacesPromise), so there's only ever one real request and
  // nothing to race. Keyed per slug (not a single slot): a third call for a
  // DIFFERENT slug landing between two calls for the SAME one must not evict
  // the first one's entry before it settles (an A → B → A navigation).
  // Holds the SHARED underlying network call only — never the bookkeeping
  // (attemptedSlug/policySlug/generation) below, which every invocation runs
  // for itself regardless of whether it started this promise or is reusing
  // it. A call that reuses an in-flight promise still has to be able to WIN
  // the eventual commit: an A → B → A navigation revisits A while its
  // original request is still pending, and that second A call must make A
  // current again (and be the one racing B's generation), not silently
  // inherit whatever the first A call decided before B ever started.
  private pendingFetches = new Map<string, Promise<TWorkspaceSessionPolicy>>();
  // An in-flight save for a slug is the freshest state about to land — a
  // fetchPolicy call for that same slug uses THIS as its source instead of
  // issuing a redundant GET of its own (which could only ever return what
  // the save is about to replace). It is a source of data only, never a
  // shortcut around the bookkeeping below: an earlier version returned this
  // promise directly, which meant a fetch that revisited a slug after
  // navigating away and back (update-A → B → A) piggybacked on A's save
  // WITHOUT taking a new generation — so once B's own, later call became
  // current, A's save's eventual commit (and the piggybacking fetch riding
  // along with it) were both skipped, and B was left live while the router
  // was back on A.
  private pendingUpdates = new Map<string, Promise<TWorkspaceSessionPolicy>>();
  // Serializes same-slug PATCHes through the server: without this, two
  // overlapping saves race as independent, unordered HTTP requests, and the
  // server can end up persisting whichever one's connection happened to
  // finish LAST on the wire — not whichever one the client most recently
  // intended. Chaining each save's actual request onto the previous one's
  // settlement (success or failure) guarantees the requests reach the
  // server in submission order, so the server's final state always matches
  // the client's most recent intent regardless of response timing.
  private updateTails = new Map<string, Promise<void>>();
  // Deliberately independent of `generation`: a GET and a PATCH racing must
  // each clear their OWN busy flag when THEY finish, regardless of which one
  // resolves first or whose data ends up winning the commit above. A single
  // generation-gated boolean would let a losing response strand the flag from
  // whichever operation actually owns it.
  private loadingCount = 0;
  private savingCount = 0;

  // One in-flight GET for the effective policy, shared by concurrent callers
  // (the layout loader and the AppShell retry hook both ask on cold load).
  // Single user-scoped resource, so a shared promise is the whole race story —
  // no generations needed.
  private pendingEffectiveFetch: Promise<TCurrentUserSessionPolicy> | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  async fetchEffectivePolicy(): Promise<TCurrentUserSessionPolicy> {
    let request = this.pendingEffectiveFetch;
    if (!request) {
      request = this.rootStore.workspaceSessionPolicyService.getEffectiveSessionPolicy();
      this.pendingEffectiveFetch = request;
      request
        .then((policy) => {
          runInAction(() => {
            this.effectivePolicy = policy;
          });
        })
        .catch((error: unknown) => {
          // Recoverable degradation: the proactive idle redirect simply stays
          // unarmed, and the server's own 401 still enforces the timeout.
          console.warn("[workspace-session-policy] effective policy fetch failed", { error });
        })
        .finally(() => {
          runInAction(() => {
            this.pendingEffectiveFetch = null;
          });
        });
    }
    return request;
  }

  async fetchPolicy(slug: string): Promise<TWorkspaceSessionPolicy> {
    // EVERY invocation does its own bookkeeping and owns its own commit
    // continuation — regardless of whether the promise it ends up awaiting
    // below is a GET it started, a GET it's sharing, or a PATCH it's
    // piggybacking on. Only the SOURCE of the data is ever shared; "does
    // this invocation's result get written to the store" is decided solely
    // by whether its own generation is still current when that source
    // settles, uniformly across all three cases.
    this.attemptedSlug = slug;
    if (this.policySlug !== slug) {
      // Loading a different workspace than the one currently held — clear
      // immediately (not just on success) so nothing renders or saves a
      // foreign workspace's policy while this fetch is in flight or if it
      // fails.
      this.policy = null;
      this.policySlug = null;
    }
    const myGeneration = ++this.generation;
    this.loadingCount++;
    this.isLoading = true;

    const source = this.pendingUpdates.get(slug) ?? this.sharedGet(slug);

    try {
      const policy = await source;
      runInAction(() => {
        if (this.generation === myGeneration) {
          this.policy = policy;
          this.policySlug = slug;
        }
      });
      return policy;
    } finally {
      runInAction(() => {
        this.loadingCount = Math.max(0, this.loadingCount - 1);
        this.isLoading = this.loadingCount > 0;
      });
    }
  }

  /** The GET for `slug`, started once and shared by every caller until it
   *  settles — never the source of currency itself (see `fetchPolicy`). */
  private sharedGet(slug: string): Promise<TWorkspaceSessionPolicy> {
    let rawFetch = this.pendingFetches.get(slug);
    if (!rawFetch) {
      rawFetch = this.rootStore.workspaceSessionPolicyService.getSessionPolicy(slug).finally(() => {
        runInAction(() => {
          if (this.pendingFetches.get(slug) === rawFetch) {
            this.pendingFetches.delete(slug);
          }
        });
      });
      this.pendingFetches.set(slug, rawFetch);
    }
    return rawFetch;
  }

  async updatePolicy(
    slug: string,
    data: TWorkspaceSessionPolicyUpdate,
    source: AnalyticsSource = "page",
  ): Promise<TWorkspaceSessionPolicy> {
    // Read before the write: `fields` is what this save changed (NG-2064).
    const fields = changedFields(
      data,
      this.policySlug === slug ? (this.policy ?? undefined) : undefined,
    );
    // A GET already in flight for this slug reflects pre-save state. Once a
    // save starts, drop it from the dedup map so no NEW fetchPolicy call can
    // adopt it later — reusing it after the save lands would hand a newer
    // (and so commit-winning) invocation a stale, pre-save response. The
    // caller(s) already sharing it keep their own direct reference and
    // still receive it normally; they just can no longer be discovered and
    // reused by a caller that starts after this point.
    this.pendingFetches.delete(slug);

    const myGeneration = ++this.generation;
    this.savingCount++;
    this.isSaving = true;

    // Chain this save's actual request onto the previous same-slug save's
    // settlement (never rejecting the chain itself — an earlier save's
    // failure must not block this one from ever reaching the server).
    const previousTail = this.updateTails.get(slug) ?? Promise.resolve();
    const rawUpdate = previousTail.then(() =>
      this.rootStore.workspaceSessionPolicyService.updateSessionPolicy(slug, data),
    );
    this.updateTails.set(
      slug,
      rawUpdate.then(
        () => undefined,
        () => undefined,
      ),
    );
    this.pendingUpdates.set(slug, rawUpdate);

    try {
      const policy = await rawUpdate;
      runInAction(() => {
        if (this.generation === myGeneration) {
          this.policy = policy;
          this.policySlug = slug;
        }
      });
      track("session_policy_updated", { fields, source });
      return policy;
    } finally {
      runInAction(() => {
        // Identity-checked, not slug-checked: two same-slug updates can
        // overlap (a second save fired before the first's response lands),
        // and the older one's cleanup must not delete the newer one's
        // still-live map entry out from under it.
        if (this.pendingUpdates.get(slug) === rawUpdate) {
          this.pendingUpdates.delete(slug);
        }
        this.savingCount = Math.max(0, this.savingCount - 1);
        this.isSaving = this.savingCount > 0;
      });
    }
  }
}
